<template>
  <component
    :is="tag"
    gk-button
    :class="[$style.button]"
    :gkbtn-color="color"
    :gkbtn-size="size"
    :disabled="_disabled"
    @click="handleClick"
  >
    <slot name="status" :disabled="disabled" :locked="locked" />
    <slot>
      {{locked ? lockLabel || label : label}}
    </slot>
    <slot name="end" :disabled="disabled" :locked="locked" />
  </component>
</template>

<script>
import VueTypes from 'vue-types'

/**
 * 按钮组件
 * @module components/Button
 * @author 徐晨
 * @see http://gitbook.geekbang.org/geek_ui/#/button
 * @example <gkButton :color="color" :size="size" :useLock="true" label="按钮文案" @click="handleClick" />
 * @example
 * <gkButton :color="color" :size="size" :useLock="true" @click="handleClick">
 *   <template slot="status" slot-scope="{disabled, locked}"></template>
 *   按钮文案
 *   <template slot="end" slot-scope="{disabled, locked}"></template>
 * </gkButton>
 */
export default {
  name: 'gkui-button',
  components: {},
  /**
   * 传入数据
   * @property {String}  [color]           按钮颜色
   * @property {String}  [size]            按钮尺寸[small, normal, big]
   * @property {Boolean} [disabled=false]  是否禁用
   * @property {Boolean} [useLock=false]   是否使用点击后锁定按钮
   * @property {String}  [label]           按钮文案
   * @property {String}  [lockLabel]       禁用时的文案
   * @property {Boolean} [stop=true]       是否阻止点击事件冒泡
   */
  props: {
    tag: VueTypes.string.def('div'),
    color: VueTypes.string.def(''),
    size: VueTypes.string.def(''),
    disabled: VueTypes.bool.def(false),
    useLock: VueTypes.bool.def(false),
    label: VueTypes.string.def(''),
    lockLabel: VueTypes.string.def(''),
    stop: VueTypes.bool.def(true)
  },
  /**
   * 内部属性
   * @member
   * @property {Boolean} locked=false  是否已锁定
   */
  data () {
    return {
      locked: false
    }
  },
  /**
   * 计算属性
   * @prop {Boolean} _disabled  按钮当前禁用的状态
   */
  computed: {
    _disabled () {
      return this.disabled || this.locked
    }
  },
  created () {
  },
  methods: {
    /**
     * @method handleClick
     * @description 点击事件，判断是否需要锁定，可点击时 $emit click 事件
     * @param {Function} event Event
     * @fires eventClick:handleClick
     */
    handleClick (event) {
      this.stop && event.stopPropagation()

      if (this._disabled) {
        return
      }

      if (this.useLock) {
        this.locked = true
      }

      /**
       * $emit 点击事件
       * @event eventClick:handleClick
       * @property {Function} clear 清除按钮锁定状态
       */
      this.$emit('click', {
        clear: () => {
          this.locked = false
        }
      })
    }
  }
}
</script>

<style lang="scss" module>
.button {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content:center;
  text-align: center;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  text-size-adjust: 100%;
  transition: all .3s ease;
  user-select: none;
  touch-action: manipulation;
  cursor: pointer;

  &:active {
    transition: none;
  }

  &[data-color=disabled] {
    cursor: not-allowed;
  }
}
</style>

